// AdminPage.js - Super Admin Component
import React, { useState, useEffect } from "react";
import {
  AppBar,
  Tabs,
  Tab,
  Paper,
  Typography,
  Button,
  Box,
  Container,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import {
  CloudUpload,
  GetApp,
  Delete,
  InsertDriveFile,
  Description,
} from "@mui/icons-material";
import { TabPanel, a11yProps } from "./TabPanel";
import axios from "axios";
import { saveAs } from "file-saver";
import AdminRegistrationForm from "./AdminRegistrationForm";
import AdminList from "./AdminList";
import CottonAdminPage from "./CottonAdminPage";
import CornAdminPage from "./CornAdminPage";
import { useNavigate } from "react-router-dom";
import ContractPrices from "./ContractPrices";
import CornContractPrices from "./CornContractPrices";
import NotificationMessage from "./NotificationMessage";

function AdminPage() {
  const [showMarketingPrices, setShowMarketingPrices] = useState(false);
  const [value, setValue] = useState(0); // Tab index for Corn/Cotton
  const [showAdminPage, setShowAdminPage] = useState(false);
  const [showFileManagement, setShowFileManagement] = useState(false);

  // File management states
  const [defaultfiles, setDefaultFiles] = useState([]);
  const [insurancefiles, setInsuranceFiles] = useState([]);
  const [marketingfiles, setMarketingFiles] = useState([]);
  const [cottondefaultfiles, setCottonDefaultFiles] = useState([]);
  const [cottoninsurancefiles, setCottonInsuranceFiles] = useState([]);
  const [cottonmarketingfiles, setCottonMarketingFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFilei, setSelectedFilei] = useState(null);
  const [selectedFilem, setSelectedFilem] = useState(null);
  const [selectedFiled, setSelectedFiled] = useState(null);
  const [loadingDownload, setLoadingDownload] = useState({});
  const [uploadingFile, setUploadingFile] = useState(null);

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const token = localStorage.getItem("token");
  let navigate = useNavigate();

  const handleCloseAdminPage = () => {
    setShowAdminPage(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  const handleMarketingPricesClick = () => {
    setShowMarketingPrices(true);
  };

  const handleAdminRegistration = () => {
    setShowAdminPage(!showAdminPage);
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    localStorage.setItem("activeAdminCrop", value === 1 ? "cotton" : "corn");
  }, [value]);

  const handleCloseContractPrices = () => {
    setShowMarketingPrices(false);
  };

  const handleManageFiles = () => {
    setShowFileManagement(true);
    // Fetch files when opening file management
    if (value === 0) {
      fetchCornFiles();
    } else {
      fetchCottonFiles();
    }
  };

  const handleCloseFileManagement = () => {
    setShowFileManagement(false);
  };

  // File handlers
  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleMarketingFileChange = (event) => {
    setSelectedFilem(event.target.files[0]);
  };

  const handleInsuranceFileChange = (event) => {
    setSelectedFilei(event.target.files[0]);
  };

  const handleDefaultFileChange = (event) => {
    setSelectedFiled(event.target.files[0]);
  };

  const handleDownload = (filePath) => {
    const fileName = decodeURIComponent(filePath.split("/").pop());
    setLoadingDownload((prev) => ({ ...prev, [fileName]: true }));

    axios
      .get(`/api/${filePath}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      })
      .then((response) => {
        const contentDisposition = response.headers["content-disposition"];
        let resolvedFileName = fileName;
        if (contentDisposition) {
          const matches = contentDisposition.match(/filename="([^"]+)"/);
          if (matches?.length === 2) {
            resolvedFileName = matches[1];
          }
        }
        saveAs(new Blob([response.data]), resolvedFileName);
      })
      .catch((error) => {
        console.error("Download error:", error);
      })
      .finally(() => {
        setLoadingDownload((prev) => ({ ...prev, [fileName]: false }));
      });
  };

  // Fetch Corn files
  const fetchCornFiles = () => {
    axios
      .get("/api/listdefaultFiles", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200) {
          setDefaultFiles(response.data.files);
        }
      })
      .catch((error) => {
        console.error("Error fetching files:", error);
      });

    axios
      .get("/api/listInsuranceFiles", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200) {
          setInsuranceFiles(response.data.files);
        }
      })
      .catch((error) => {
        console.error("Error fetching files:", error);
      });

    axios
      .get("/api/listMarketingFiles", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200) {
          setMarketingFiles(response.data.files);
        }
      })
      .catch((error) => {
        console.error("Error fetching files:", error);
      });
  };

  // Fetch Cotton files
  const fetchCottonFiles = () => {
    axios
      .get("/api/listCottonDefaultFiles", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200) {
          setCottonDefaultFiles(response.data.files);
        }
      })
      .catch((error) => {
        console.error("Error fetching files:", error);
      });

    axios
      .get("/api/listCottonInsuranceFiles", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200) {
          setCottonInsuranceFiles(response.data.files);
        }
      })
      .catch((error) => {
        console.error("Error fetching files:", error);
      });

    axios
      .get("/api/listCottonMarketingFiles", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200) {
          setCottonMarketingFiles(response.data.files);
        }
      })
      .catch((error) => {
        console.error("Error fetching files:", error);
      });
  };

  // Upload handlers for Corn
  const handleInsuranceFileUpload = () => {
    if (!selectedFilei) {
      showNotification("Please select a file to upload.", "warning");
      return;
    }

    setUploadingFile("insurance");
    const formData = new FormData();
    formData.append("file", selectedFilei);

    axios
      .post("/api/uploadInsuranceFile", formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        showNotification("Insurance file uploaded successfully", "success");
        document.getElementById("insuranceFileInput").value = "";
        setSelectedFilei(null);
        fetchCornFiles();
      })
      .catch((error) => {
        console.error("Error uploading file:", error);
        showNotification("Error uploading file", "error");
      });
  };

  const handleMarketingOptionsFileUpload = () => {
    if (!selectedFilem) {
      showNotification("Please select a file to upload.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFilem);

    axios
      .post("/api/uploadMarketingOptionsFile", formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        showNotification("Marketing file uploaded successfully", "success");
        document.getElementById("marketingOptionsFileInput").value = "";
        setSelectedFilem(null);
        fetchCornFiles();
      })
      .catch((error) => {
        console.error("Error uploading file:", error);
        showNotification("Error uploading file", "error");
      });
  };

  const handlecorndefaultFileUpload = () => {
    if (!selectedFiled) {
      showNotification("Please select a file to upload.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFiled);

    axios
      .post("/api/uploadDefaultFile", formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        showNotification("General file uploaded successfully", "success");
        document.getElementById("defaultFileInput").value = "";
        setSelectedFiled(null);
        fetchCornFiles();
      })
      .catch((error) => {
        console.error("Error uploading file:", error);
        showNotification("Error uploading file", "error");
      });
  };

  // Upload handlers for Cotton
  const handleCottonInsuranceFileUpload = () => {
    const fileInput = document.getElementById("insuranceFileInput1");
    const file = fileInput?.files[0];

    if (!file) {
      showNotification("Please select a file to upload.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    axios
      .post("/api/uploadCottonInsuranceFile", formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        showNotification("Insurance file uploaded successfully", "success");
        fileInput.value = "";
        fetchCottonFiles();
      })
      .catch((error) => {
        console.error("Error uploading file:", error);
        showNotification("Error uploading file", "error");
      });
  };

  const handleCottonMarketingOptionsFileUpload = () => {
    const fileInput = document.getElementById("marketingOptionsFileInput1");
    const file = fileInput?.files[0];

    if (!file) {
      showNotification("Please select a file to upload.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    axios
      .post("/api/uploadCottonMarketingOptionsFile", formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        showNotification("Marketing file uploaded successfully", "success");
        fileInput.value = "";
        fetchCottonFiles();
      })
      .catch((error) => {
        console.error("Error uploading file:", error);
        showNotification("Error uploading file", "error");
      });
  };

  const handledefaultFileUpload = () => {
    const fileInput = document.getElementById("defaultCottonFileInput");
    const file = fileInput?.files[0];

    if (!file) {
      showNotification("Please select a file to upload.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    axios
      .post("/api/uploadCottonDefaultFile", formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        showNotification("General file uploaded successfully", "success");
        fileInput.value = "";
        fetchCottonFiles();
      })
      .catch((error) => {
        console.error("Error uploading file:", error);
        showNotification("Error uploading file", "error");
      });
  };

  // Delete handlers for Corn
  const handleinsuranceDelete = (fileName) => {
    axios
      .delete(`/api/deleteinsuranceFile/${fileName}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200) {
          showNotification("File deleted successfully", "success");
          fetchCornFiles();
        }
      })
      .catch((error) => {
        console.error("Error deleting file:", error);
        showNotification("Error deleting file", "error");
      });
  };

  const handlemarketingDelete = (fileName) => {
    axios
      .delete(`/api/deletemarketingFile/${encodeURIComponent(fileName)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200) {
          showNotification("File deleted successfully", "success");
          fetchCornFiles();
        }
      })
      .catch((error) => {
        console.error("Error deleting file:", error);
        showNotification("Error deleting file", "error");
      });
  };

  const handledefaultDelete = (fileName) => {
    axios
      .delete(`/api/deletedefaultFile/${fileName}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200) {
          showNotification("File deleted successfully", "success");
          fetchCornFiles();
        }
      })
      .catch((error) => {
        console.error("Error deleting file:", error);
        showNotification("Error deleting file", "error");
      });
  };

  // Delete handlers for Cotton
  const handlecottoninsuranceDelete = (fileName) => {
    axios
      .delete(`/api/deletecottoninsuranceFile/${fileName}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200) {
          showNotification("File deleted successfully", "success");
          fetchCottonFiles();
        }
      })
      .catch((error) => {
        console.error("Error deleting file:", error);
        showNotification("Error deleting file", "error");
      });
  };

  const handlecottonmarketingDelete = (fileName) => {
    axios
      .delete(`/api/deletecottonmarketingFile/${fileName}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200) {
          showNotification("File deleted successfully", "success");
          fetchCottonFiles();
        }
      })
      .catch((error) => {
        console.error("Error deleting file:", error);
        showNotification("Error deleting file", "error");
      });
  };

  const handlecottondefaultDelete = (fileName) => {
    axios
      .delete(`/api/deletecottondefaultFile/${fileName}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200) {
          showNotification("File deleted successfully", "success");
          fetchCottonFiles();
        }
      })
      .catch((error) => {
        console.error("Error deleting file:", error);
        showNotification("Error deleting file", "error");
      });
  };

  // Show Admin Registration/List page
  if (showAdminPage) {
    return (
      <div>
        <AdminRegistrationForm onClose={handleCloseAdminPage} />
        <AdminList />
      </div>
    );
  }

  // Show Marketing Prices page
  if (showMarketingPrices) {
    return (
      <div style={{ marginLeft: "20px" }}>
        <Button onClick={handleCloseContractPrices}>Back to Main Page</Button>
        {value === 0 ? <CornContractPrices /> : <ContractPrices />}
      </div>
    );
  }

  // Show File Management page
  if (showFileManagement) {
    return (
      <div style={{ padding: "20px" }}>
        <Button
          onClick={handleCloseFileManagement}
          variant="contained"
          style={{ marginBottom: "20px" }}
        >
          Back to Main Page
        </Button>
        <Typography variant="h4" component="h2" gutterBottom>
          {value === 0 ? "Corn" : "Cotton"} Files Management
        </Typography>

        {value === 0 ? (
          // Corn Files Management
          <div>
            <Card elevation={3} sx={{ mb: 3, p: 2 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  color="primary"
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <CloudUpload sx={{ mr: 1 }} /> Upload Files
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Insurance Selection
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Choose a file that contains details for teams to check
                    insurance options
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<InsertDriveFile />}
                    >
                      Choose File
                      <input
                        type="file"
                        id="insuranceFileInput"
                        onChange={handleInsuranceFileChange}
                        hidden
                      />
                    </Button>
                    {selectedFilei && (
                      <Chip
                        label={selectedFilei.name}
                        onDelete={() => {
                          setSelectedFilei(null);
                          document.getElementById("insuranceFileInput").value =
                            "";
                        }}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    <Button
                      variant="contained"
                      onClick={handleInsuranceFileUpload}
                      disabled={!selectedFilei || uploadingFile === "insurance"}
                      startIcon={
                        uploadingFile === "insurance" ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CloudUpload />
                        )
                      }
                    >
                      {uploadingFile === "insurance"
                        ? "Uploading..."
                        : "Upload Insurance"}
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Marketing Options
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Choose a file that contains details for teams to check
                    marketing options
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<InsertDriveFile />}
                    >
                      Choose File
                      <input
                        type="file"
                        id="marketingOptionsFileInput"
                        onChange={handleMarketingFileChange}
                        hidden
                      />
                    </Button>
                    {selectedFilem && (
                      <Chip
                        label={selectedFilem.name}
                        onDelete={() => {
                          setSelectedFilem(null);
                          document.getElementById(
                            "marketingOptionsFileInput",
                          ).value = "";
                        }}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    <Button
                      variant="contained"
                      onClick={handleMarketingOptionsFileUpload}
                      disabled={!selectedFilem || uploadingFile === "marketing"}
                      startIcon={
                        uploadingFile === "marketing" ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CloudUpload />
                        )
                      }
                    >
                      {uploadingFile === "marketing"
                        ? "Uploading..."
                        : "Upload Marketing"}
                    </Button>
                  </Box>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    General Files
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Choose a file to upload for all teams
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<InsertDriveFile />}
                    >
                      Choose File
                      <input
                        type="file"
                        id="defaultFileInput"
                        onChange={handleDefaultFileChange}
                        hidden
                      />
                    </Button>
                    {selectedFiled && (
                      <Chip
                        label={selectedFiled.name}
                        onDelete={() => {
                          setSelectedFiled(null);
                          document.getElementById("defaultFileInput").value =
                            "";
                        }}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    <Button
                      variant="contained"
                      onClick={handlecorndefaultFileUpload}
                      disabled={!selectedFiled || uploadingFile === "default"}
                      startIcon={
                        uploadingFile === "default" ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CloudUpload />
                        )
                      }
                    >
                      {uploadingFile === "default"
                        ? "Uploading..."
                        : "Upload Default"}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Container>
              <Paper
                elevation={3}
                style={{ padding: "20px", marginTop: "20px" }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Description sx={{ mr: 1 }} /> Corn Files
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="h6" gutterBottom color="primary">
                  Default Files:
                </Typography>
                <List>
                  {defaultfiles
                    .filter(
                      (fileName) =>
                        fileName !== "metadata.json" &&
                        fileName !== "Thumbs.db",
                    )
                    .map((fileName, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          mb: 1,
                          "&:hover": { bgcolor: "#f5f5f5" },
                        }}
                      >
                        <InsertDriveFile sx={{ mr: 2, color: "#1976d2" }} />
                        <ListItemText primary={fileName} />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          disabled={!!loadingDownload[fileName]}
                          onClick={() =>
                            handleDownload(`downloadDefaultFile/${fileName}`)
                          }
                          startIcon={
                            loadingDownload[fileName] ? null : <GetApp />
                          }
                          sx={{ mr: 1 }}
                        >
                          {loadingDownload[fileName] ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Download"
                          )}
                        </Button>
                        <IconButton
                          color="error"
                          onClick={() => handledefaultDelete(fileName)}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </ListItem>
                    ))}
                </List>

                <Typography
                  variant="h6"
                  gutterBottom
                  color="primary"
                  sx={{ mt: 3 }}
                >
                  Insurance Files:
                </Typography>
                <List>
                  {insurancefiles
                    .filter(
                      (fileName) =>
                        fileName !== "metadata.json" &&
                        fileName !== "Thumbs.db",
                    )
                    .map((fileName, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          mb: 1,
                          "&:hover": { bgcolor: "#f5f5f5" },
                        }}
                      >
                        <InsertDriveFile sx={{ mr: 2, color: "#1976d2" }} />
                        <ListItemText primary={fileName} />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          disabled={!!loadingDownload[fileName]}
                          onClick={() =>
                            handleDownload(`downloadInsuranceFile/${fileName}`)
                          }
                          startIcon={
                            loadingDownload[fileName] ? null : <GetApp />
                          }
                          sx={{ mr: 1 }}
                        >
                          {loadingDownload[fileName] ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Download"
                          )}
                        </Button>
                        <IconButton
                          color="error"
                          onClick={() => handleinsuranceDelete(fileName)}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </ListItem>
                    ))}
                </List>

                <Typography
                  variant="h6"
                  gutterBottom
                  color="primary"
                  sx={{ mt: 3 }}
                >
                  Marketing Files:
                </Typography>
                <List>
                  {marketingfiles
                    .filter(
                      (fileName) =>
                        fileName !== "metadata.json" &&
                        fileName !== "Thumbs.db",
                    )
                    .map((fileName, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          mb: 1,
                          "&:hover": { bgcolor: "#f5f5f5" },
                        }}
                      >
                        <InsertDriveFile sx={{ mr: 2, color: "#1976d2" }} />
                        <InsertDriveFile sx={{ mr: 2, color: "#1976d2" }} />
                        <ListItemText primary={fileName} />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          disabled={!!loadingDownload[fileName]}
                          onClick={() =>
                            handleDownload(`downloadMarketingFile/${fileName}`)
                          }
                          startIcon={
                            loadingDownload[fileName] ? null : <GetApp />
                          }
                          sx={{ mr: 1 }}
                        >
                          {loadingDownload[fileName] ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Download"
                          )}
                        </Button>
                        <IconButton
                          color="error"
                          onClick={() => handlemarketingDelete(fileName)}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </ListItem>
                    ))}
                </List>
              </Paper>
            </Container>
          </div>
        ) : (
          // Cotton Files Management
          <div>
            <Card elevation={3} sx={{ mb: 3, p: 2 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  color="primary"
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <CloudUpload sx={{ mr: 1 }} /> Upload Files
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Insurance Selection
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Choose a file that contains details for teams to check
                    insurance options
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<InsertDriveFile />}
                    >
                      Choose File
                      <input type="file" id="insuranceFileInput1" hidden />
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleCottonInsuranceFileUpload}
                      disabled={uploadingFile === "cottonInsurance"}
                      startIcon={
                        uploadingFile === "cottonInsurance" ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CloudUpload />
                        )
                      }
                    >
                      {uploadingFile === "cottonInsurance"
                        ? "Uploading..."
                        : "Upload Insurance"}
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Marketing Options
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Choose a file that contains details for teams to check
                    marketing options
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<InsertDriveFile />}
                    >
                      Choose File
                      <input
                        type="file"
                        id="marketingOptionsFileInput1"
                        hidden
                      />
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleCottonMarketingOptionsFileUpload}
                      disabled={uploadingFile === "cottonMarketing"}
                      startIcon={
                        uploadingFile === "cottonMarketing" ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CloudUpload />
                        )
                      }
                    >
                      {uploadingFile === "cottonMarketing"
                        ? "Uploading..."
                        : "Upload Marketing"}
                    </Button>
                  </Box>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    General Files
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Choose a file to upload for all teams
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<InsertDriveFile />}
                    >
                      Choose File
                      <input type="file" id="defaultCottonFileInput" hidden />
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handledefaultFileUpload}
                      disabled={uploadingFile === "cottonDefault"}
                      startIcon={
                        uploadingFile === "cottonDefault" ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CloudUpload />
                        )
                      }
                    >
                      {uploadingFile === "cottonDefault"
                        ? "Uploading..."
                        : "Upload Default"}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Container>
              <Paper
                elevation={3}
                style={{ padding: "20px", marginTop: "20px" }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Description sx={{ mr: 1 }} /> Cotton Files
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="h6" gutterBottom color="primary">
                  Default Files:
                </Typography>
                <List>
                  {cottondefaultfiles
                    .filter(
                      (fileName) =>
                        fileName !== "metadata.json" &&
                        fileName !== "Thumbs.db",
                    )
                    .map((fileName, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          mb: 1,
                          "&:hover": { bgcolor: "#f5f5f5" },
                        }}
                      >
                        <InsertDriveFile sx={{ mr: 2, color: "#1976d2" }} />
                        <ListItemText primary={fileName} />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          disabled={!!loadingDownload[fileName]}
                          onClick={() =>
                            handleDownload(
                              `downloadDefaultCottonFile/${fileName}`,
                            )
                          }
                          startIcon={
                            loadingDownload[fileName] ? null : <GetApp />
                          }
                          sx={{ mr: 1 }}
                        >
                          {loadingDownload[fileName] ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Download"
                          )}
                        </Button>
                        <IconButton
                          color="error"
                          onClick={() => handlecottondefaultDelete(fileName)}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </ListItem>
                    ))}
                </List>

                <Typography
                  variant="h6"
                  gutterBottom
                  color="primary"
                  sx={{ mt: 3 }}
                >
                  Insurance Files:
                </Typography>
                <List>
                  {cottoninsurancefiles
                    .filter(
                      (fileName) =>
                        fileName !== "metadata.json" &&
                        fileName !== "Thumbs.db",
                    )
                    .map((fileName, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          mb: 1,
                          "&:hover": { bgcolor: "#f5f5f5" },
                        }}
                      >
                        <InsertDriveFile sx={{ mr: 2, color: "#1976d2" }} />
                        <ListItemText primary={fileName} />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          disabled={!!loadingDownload[fileName]}
                          onClick={() =>
                            handleDownload(
                              `downloadInsuranceCottonFile/${fileName}`,
                            )
                          }
                          startIcon={
                            loadingDownload[fileName] ? null : <GetApp />
                          }
                          sx={{ mr: 1 }}
                        >
                          {loadingDownload[fileName] ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Download"
                          )}
                        </Button>
                        <IconButton
                          color="error"
                          onClick={() => handlecottoninsuranceDelete(fileName)}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </ListItem>
                    ))}
                </List>

                <Typography
                  variant="h6"
                  gutterBottom
                  color="primary"
                  sx={{ mt: 3 }}
                >
                  Marketing Files:
                </Typography>
                <List>
                  {cottonmarketingfiles
                    .filter(
                      (fileName) =>
                        fileName !== "metadata.json" &&
                        fileName !== "Thumbs.db",
                    )
                    .map((fileName, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          mb: 1,
                          "&:hover": { bgcolor: "#f5f5f5" },
                        }}
                      >
                        <InsertDriveFile sx={{ mr: 2, color: "#1976d2" }} />
                        <ListItemText primary={fileName} />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          disabled={!!loadingDownload[fileName]}
                          onClick={() =>
                            handleDownload(
                              `downloadMarketingCottonFile/${fileName}`,
                            )
                          }
                          startIcon={
                            loadingDownload[fileName] ? null : <GetApp />
                          }
                          sx={{ mr: 1 }}
                        >
                          {loadingDownload[fileName] ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Download"
                          )}
                        </Button>
                        <IconButton
                          color="error"
                          onClick={() => handlecottonmarketingDelete(fileName)}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </ListItem>
                    ))}
                </List>
              </Paper>
            </Container>
          </div>
        )}
      </div>
    );
  }

  // Main Super Admin Dashboard
  return (
    <div>
      <AppBar position="static" color="default">
        <Paper elevation={3}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="Admin Tabs"
            variant="fullWidth"
            indicatorColor="secondary"
            textColor="primary"
          >
            <Tab label="Corn" {...a11yProps(0)} />
            <Tab label="Cotton" {...a11yProps(1)} />
          </Tabs>
        </Paper>
      </AppBar>

      <Paper
        elevation={2}
        style={{
          padding: 20,
          margin: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" gutterBottom>
          Welcome Super Admin!
        </Typography>
        <div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAdminRegistration}
            style={{ marginRight: 10 }}
          >
            Manage Admins
          </Button>
          <Button
            variant="contained"
            color="primary"
            style={{ marginRight: 10 }}
            onClick={handleMarketingPricesClick}
          >
            Contract Prices
          </Button>
          <Button
            variant="contained"
            color="primary"
            style={{ marginRight: 10 }}
            onClick={() =>
              navigate("/fielddata", {
                state: { cropType: value === 1 ? "cotton" : "corn" },
              })
            }
          >
            Field Data Management
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleManageFiles}
            style={{ marginRight: 10 }}
          >
            Manage Files
          </Button>
          <Button variant="contained" color="primary" onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </Paper>

      <TabPanel value={value} index={0}>
        <CornAdminPage showSuperAdminFeatures={true} />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <CottonAdminPage showSuperAdminFeatures={true} />
      </TabPanel>
      <NotificationMessage
        open={notification.open}
        onClose={handleCloseNotification}
        message={notification.message}
        severity={notification.severity}
      />
    </div>
  );
}

export default AdminPage;
